import { useEffect, useRef, useState, useCallback } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import {
  LayoutDashboard, Search, Users, Car, IdCard, Building2, Upload, BarChart3,
  ScrollText, UserCog, ShieldCheck, Settings, Bell, Sun, Moon, LogOut,
  PanelLeftClose, PanelLeftOpen, User, ChevronsUpDown,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { API } from "../lib/api";
import { ROLE_BADGE_COLORS } from "../lib/constants";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";

const MENU = [
  { to: "/", label: "Tableau de bord", icon: LayoutDashboard, end: true, testid: "nav-dashboard", perm: "VIEW_DASHBOARD" },
  { to: "/recherche", label: "Recherche", icon: Search, perm: "SEARCH_DRIVER", testid: "nav-search" },
  { to: "/conducteurs", label: "Conducteurs", icon: Users, perm: "VIEW_DRIVER", testid: "nav-drivers" },
  { to: "/vehicules", label: "Vehicules", icon: Car, perm: "VIEW_VEHICLE", testid: "nav-vehicles" },
  { to: "/permis", label: "Permis", icon: IdCard, perm: "VIEW_LICENSE", testid: "nav-licenses" },
  { to: "/structures", label: "Structures", icon: Building2, perm: "VIEW_ORGANIZATION", testid: "nav-structures" },
  { to: "/importation", label: "Importation", icon: Upload, perm: "IMPORT_DATA", testid: "nav-import" },
  { to: "/statistiques", label: "Statistiques", icon: BarChart3, perm: "VIEW_STATISTICS", testid: "nav-stats" },
];

const ADMIN_MENU = [
  { to: "/admin/utilisateurs", label: "Utilisateurs", icon: UserCog, perm: "MANAGE_USERS", testid: "nav-users" },
  { to: "/admin/securite", label: "Securite", icon: ShieldCheck, perm: "MANAGE_ROLES", testid: "nav-security" },
  { to: "/historique", label: "Journal d'audit", icon: ScrollText, perm: "VIEW_AUDIT_LOGS", testid: "nav-audit" },
  { to: "/admin/parametres", label: "Parametres", icon: Settings, perm: "MANAGE_SETTINGS", testid: "nav-settings" },
];

const INACTIVITY_LIMIT = 30 * 60 * 1000;

export default function Layout() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout, hasPerm } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [notifs, setNotifs] = useState({ items: [], unread: 0 });
  const timerRef = useRef(null);

  const loadNotifs = useCallback(() => {
    API.get("/notifications").then((r) => setNotifs(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    loadNotifs();
    const t = setInterval(loadNotifs, 30000);
    return () => clearInterval(t);
  }, [loadNotifs]);

  useEffect(() => {
    const reset = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(async () => {
        await logout();
        toast.warning("Session expiree pour inactivite.");
        navigate("/connexion");
      }, INACTIVITY_LIMIT);
    };
    const events = ["mousemove", "keydown", "click"];
    let last = 0;
    const throttled = () => {
      const now = Date.now();
      if (now - last > 5000) { last = now; reset(); }
    };
    events.forEach((e) => window.addEventListener(e, throttled));
    reset();
    return () => {
      events.forEach((e) => window.removeEventListener(e, throttled));
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [logout, navigate]);

  const doLogout = async () => {
    await logout();
    toast.success("Vous etes deconnecte.");
    navigate("/connexion");
  };

  const visibleMenu = MENU.filter((m) => !m.perm || hasPerm(m.perm));
  const visibleAdmin = ADMIN_MENU.filter((m) => !m.perm || hasPerm(m.perm));

  const sidebarContent = (
    <>
      <div className="flex items-center gap-3 px-4 h-16 border-b border-white/10">
        <img src="/logo.png" alt="Logo de la Republique Gabonaise" className="h-8 w-9 rounded-md object-contain bg-white" data-testid="sidebar-logo" />
        {!collapsed && (
          <div className="min-w-0">
            <p className="font-heading font-bold text-white text-lg leading-tight tracking-wide">ALARA</p>
            <p className="text-[10px] text-slate-400 truncate">Republique Gabonaise</p>
          </div>
        )}
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
        {visibleMenu.map((m) => (
          <NavLink key={m.to} to={m.to} end={m.end} data-testid={m.testid}
            className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""} ${collapsed ? "justify-center" : ""}`}
            title={m.label} onClick={() => setMobileOpen(false)}>
            <m.icon className="h-5 w-5 shrink-0" />
            {!collapsed && <span>{m.label}</span>}
          </NavLink>
        ))}
        {visibleAdmin.length > 0 && (
          <>
            {!collapsed && <p className="px-3 pt-5 pb-1 text-[10px] font-semibold uppercase tracking-widest text-slate-500">Administration</p>}
            {collapsed && <div className="mx-3 my-3 border-t border-white/10" />}
            {visibleAdmin.map((m) => (
              <NavLink key={m.to} to={m.to} data-testid={m.testid}
                className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""} ${collapsed ? "justify-center" : ""}`}
                title={m.label} onClick={() => setMobileOpen(false)}>
                <m.icon className="h-5 w-5 shrink-0" />
                {!collapsed && <span>{m.label}</span>}
              </NavLink>
            ))}
          </>
        )}
      </nav>
      <div className="mt-auto">
        <div className="tricolor" />
        {!collapsed && (
          <p className="text-center text-[9px] text-slate-500 py-2 tracking-widest">UNION - TRAVAIL - JUSTICE</p>
        )}
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-background overflow-x-clip">
      <aside className={`fixed left-0 top-0 z-40 hidden md:flex h-screen flex-col bg-[#0B132B] dark:bg-[#050811] transition-all duration-200 ${collapsed ? "w-20" : "w-64"}`} data-testid="sidebar">
        {sidebarContent}
      </aside>
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-64 flex flex-col bg-[#0B132B]" data-testid="sidebar-mobile">{sidebarContent}</aside>
        </div>
      )}

      <div className={`print-reset min-w-0 transition-all duration-200 ${collapsed ? "md:ml-20" : "md:ml-64"}`}>
        <div className="tricolor sticky top-0 z-40" />
        <header className="sticky top-1.5 z-30 flex h-16 items-center gap-3 border-b bg-white/90 dark:bg-[#0d1420]/90 backdrop-blur-md px-4" data-testid="header">
          <button
            data-testid="sidebar-toggle"
            className="rounded-lg p-2 hover:bg-accent transition-colors"
            onClick={() => (window.innerWidth < 768 ? setMobileOpen(true) : setCollapsed(!collapsed))}
          >
            {collapsed ? <PanelLeftOpen className="h-5 w-5" /> : <PanelLeftClose className="h-5 w-5" />}
          </button>
          <div className="hidden md:block min-w-0">
            <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-[#009E60]">Republique Gabonaise</p>
            <p className="text-sm font-semibold leading-tight truncate" data-testid="header-ministry">Ministere des Transports, de la Marine Marchande, charge de la Logistique</p>
          </div>
          <div className="flex-1" />

          <DropdownMenu onOpenChange={(open) => { if (open) { loadNotifs(); API.post("/notifications/read-all").then(loadNotifs).catch(() => {}); } }}>
            <DropdownMenuTrigger asChild>
              <button className="relative rounded-lg p-2 hover:bg-accent transition-colors" data-testid="notifications-bell">
                <Bell className="h-5 w-5" />
                {notifs.unread > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#FCD116] px-1 text-[10px] font-bold text-slate-900" data-testid="notifications-count">
                    {notifs.unread}
                  </span>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80" data-testid="notifications-menu">
              <DropdownMenuLabel>Notifications</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {notifs.items.length === 0 && <p className="px-3 py-4 text-sm text-muted-foreground">Aucune notification.</p>}
              {notifs.items.slice(0, 8).map((n) => (
                <DropdownMenuItem key={n.id} className="flex flex-col items-start gap-0.5 py-2" data-testid={`notification-${n.id}`}>
                  <span className="text-sm font-medium">{n.title}</span>
                  <span className="text-xs text-muted-foreground line-clamp-2">{n.message}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <button onClick={toggle} className="rounded-lg p-2 hover:bg-accent transition-colors" data-testid="theme-toggle" title={theme === "dark" ? "Mode clair" : "Mode sombre"}>
            {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-accent transition-colors" data-testid="user-menu">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#009E60] text-white text-sm font-bold">
                  {(user?.first_name?.[0] || "U").toUpperCase()}
                </span>
                <span className="hidden sm:block text-left">
                  <span className="block text-sm font-medium leading-tight">{user?.first_name} {user?.last_name}</span>
                  <Badge className={`text-[10px] px-1.5 py-0 ${ROLE_BADGE_COLORS[user?.role] || ""}`} data-testid="user-role-badge">{user?.role_name || user?.role}</Badge>
                </span>
                <ChevronsUpDown className="h-4 w-4 text-muted-foreground" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56" data-testid="user-menu-dropdown">
              <DropdownMenuLabel>
                <p className="text-sm font-medium">{user?.first_name} {user?.last_name}</p>
                <p className="text-xs text-muted-foreground">{user?.email}</p>
                {user?.service && <p className="text-xs text-muted-foreground">{user.service}</p>}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/profil" className="flex items-center gap-2" data-testid="menu-profile"><User className="h-4 w-4" /> Mon compte</Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={doLogout} className="text-red-600 dark:text-red-400" data-testid="menu-logout">
                <LogOut className="h-4 w-4 mr-2" /> Deconnexion
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <main className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto w-full min-w-0">
          <Outlet />
        </main>
        <footer className="border-t px-6 py-4 text-center text-xs text-muted-foreground">
          ALARA — Ministere des Transports, de la Marine Marchande, charge de la Logistique — Republique Gabonaise
        </footer>
      </div>
    </div>
  );
}
